import { FC } from "react";
import { Empty } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Hotel, StoredHotelBookingData } from "src/store/slices/hotels/Hotels";
import { HotelCard } from "src/Modules/Hotels/Components";
import HotelCardSkeleton from "src/Modules/Hotels/Components/HotelCard/HotelCardSkeleton";
import { useHotelsListingContext } from "../../context/HotelsListingContext";
import { useNavigate } from "react-router-dom";
import { simpleHash } from "src/utility";
import { useSelector } from "react-redux";
import { RootState } from "src/store";

const HotelList: FC<{ hotels: Hotel[] }> = ({ hotels }) => {
  const { t } = useTranslation();
  const { pointsFactor, isFetching, isPollingDone, searchObject, sessionId } = useHotelsListingContext();
  const navigate = useNavigate();
  const { startedAt, maxTime } = useSelector((state: RootState) => state.timer);

  const numberOfNights =
    searchObject?.checkin && searchObject?.checkout
      ? Math.max(1, dayjs(searchObject.checkout).diff(dayjs(searchObject.checkin), "day"))
      : 1;

  const totalGuests = searchObject?.rooms?.reduce((sum, r) => sum + (r.adults ?? 0) + (r.children ?? 0), 0) ?? 0;

  if (!hotels.length && isFetching)
    return (
      <div className="flex w-full flex-col gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <HotelCardSkeleton key={i} />
        ))}
      </div>
    );

  if (!hotels.length && isPollingDone)
    return (
      <div className="flex items-center justify-center px-6 py-16 md:p-50">
        <Empty description={<div className="mx-auto w-105">{t("Hotels.listing.noHotelsFound")}</div>} />
      </div>
    );

  return (
    <>
      <div className="flex w-full flex-col gap-6">
        {hotels.map((hotel) => (
          <HotelCard
            key={hotel.hotelCode}
            hotelData={hotel}
            pointsFactor={pointsFactor}
            numberOfNights={numberOfNights}
            totalGuests={totalGuests}
            onBookNow={() => {
              const payload: StoredHotelBookingData = { hotel, pointsFactor, sessionId, _session: { startedAt, maxTime } };
              const json = JSON.stringify(payload);
              const { hash } = simpleHash(json);
              const key = `hotel-${hash}`;
              localStorage.setItem(key, json);
              navigate(`hotel/${key}`);
            }}
          />
        ))}
      </div>
    </>
  );
};

export default HotelList;
